/**
 * SHOWS THE UPDATE QUESTION ONCE THE WATCH SAYS THE SERVER HAS MOVED.
 *
 * THE CHUNK IS FETCHED AT STARTUP, NOT WHEN THE DEPLOY IS NOTICED. A deploy
 * deletes the previous `/assets/`. The question lives in one of those files.
 * Waiting to download it until the check has already seen the new
 * `index.html` asks for a name the server no longer has, and the reminder
 * never appears. The import stays dynamic so the dialogue, and the modal it
 * is built from, stay out of first paint. A page that noted no release (dev)
 * never fetches it.
 *
 * If that early fetch fails and a later check still finds a new release, the
 * page reloads. There is no question left to show, and staying on the
 * previous build is the worse of the two.
 */
import { useEffect, useState, type ComponentType } from "react";
import { notedShell, useShellUpdate } from "@/lib/shell-update";

export function ShellUpdateHost() {
  const { open, later } = useShellUpdate();
  const [Dialogue, setDialogue] = useState<ComponentType<{ onLater: () => void }> | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (notedShell() === null) return;
    let stopped = false;
    void import("./update-dialogue")
      .then((mod) => {
        if (!stopped) setDialogue(() => mod.UpdateDialogue);
      })
      .catch(() => {
        if (!stopped) setFailed(true);
      });
    return () => {
      stopped = true;
    };
  }, []);

  useEffect(() => {
    if (open && failed) window.location.reload();
  }, [open, failed]);

  if (!open || Dialogue === null) return null;
  return <Dialogue onLater={later} />;
}
